import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { tokens, Badge } from '@hotjar/hotsauce';

const Container = styled.div<{ highlighted: boolean }>`
  padding-top: 10px;
  padding-bottom: 10px;
  border-radius: 3px;
  ${({ highlighted }) =>
    highlighted &&
    css`
      color: ${tokens.color.text_default};
      background: ${tokens.color.bg_active};
      padding-left: 10px;
      padding-right: 10px;
      margin-bottom: 16px;
    `};
`;

const TotalContainer = styled.span`
  display: inline-grid;
  grid-template-columns: auto 1fr;
  grid-gap: ${tokens.spacer.x1};
  align-items: center;
`;

const Label = styled.label`
  font-size: 11px;
  color: ${tokens.color.text_light};
  font-weight: normal;
`;

const Full = styled.span`
  font-size: 13px;
  text-decoration: line-through;
  opacity: 0.6;
`;

const Note = styled.div`
  font-size: 12px;
  opacity: 0.6;
`;

interface Props {
  billingCycle: string;
  planDowngradePeriod: boolean;
  total: string;
  showSubtotal: boolean;
  subtotal: string;
  discountPercentage?: string;
  showAgencyDiscount: boolean;
  legacyDiscount: string | undefined;
  highlighted: boolean;
  bundleDiscount?: boolean;
}

export function SitePrice(props: Props): JSX.Element {
  const {
    billingCycle,
    planDowngradePeriod,
    total,
    showSubtotal,
    subtotal,
    discountPercentage,
    showAgencyDiscount,
    legacyDiscount,
    highlighted,
    bundleDiscount,
  } = props;
  const { t } = useTranslation();
  const isYearly = ['yearly_recurring', 'yearly'].includes(billingCycle);

  const badgeBackgroundColor = highlighted ? tokens.color.white : tokens.color.ui_active;
  const badgeTextColor = highlighted ? tokens.color.ui_active : tokens.color.white;

  return (
    <>
      <Label>
        {t('billing:site-box.cost')} /{' '}
        {isYearly ? t('billing:site-box.year') : t('billing:site-box.month')}
        {planDowngradePeriod && (
          <span className="no-transform"> {t('billing:site-box.next-bill')}</span>
        )}
      </Label>
      <Container highlighted={highlighted}>
        <TotalContainer>
          <span>
            {total} {showSubtotal && <Full>{subtotal}</Full>}
          </span>

          {legacyDiscount && (
            <Tooltip
              content={
                <Trans i18nKey="billing:site-box.legacy-discount" values={{ legacyDiscount }}>
                  You will continue to get a <strong>{legacyDiscount}</strong>/month legacy discount
                  as long as you maintain a paid subscription on this site.
                </Trans>
              }
            >
              <Badge kind="custom" color={badgeBackgroundColor} textColor={badgeTextColor}>
                {t('billing:site-box.legacy-price')}
              </Badge>
            </Tooltip>
          )}
          {showAgencyDiscount && (
            <>
              <span>&nbsp;</span>
              <Note>{t('billing:site-box.agency-discount', { discountPercentage })}</Note>
            </>
          )}
          {bundleDiscount && !legacyDiscount && !showAgencyDiscount && (
            <>
              <Badge kind="custom" color={badgeBackgroundColor} textColor={badgeTextColor}>
                {t('billing:site-box.bundle-discount-badge')}
              </Badge>
              <span>&nbsp;</span>
              <Note>
                {t('billing:site-box.bundle-discount-info', { discountPercentage: '20' })}
              </Note>
            </>
          )}
        </TotalContainer>
      </Container>
    </>
  );
}
